<script setup lang="ts">
/**
 * PageHelp — collapsible explanation panel сверху страницы.
 *
 * Pattern из Stripe / Linear / Customer.io: на каждой не-тривиальной странице
 * админ-видит первым делом «Что это / Зачем / Как использовать» card,
 * который можно свернуть. Состояние persist в localStorage per-key, чтобы
 * опытные пользователи не видели одно и то же каждый день.
 *
 * Usage:
 *   <PageHelp storage-key="page-help-waiting-baskets" title="Что это такое">
 *     <p>Корзина ожидания — это…</p>
 *     <p>Зачем нужна — …</p>
 *     <h4>Пример</h4>
 *     <p>…</p>
 *   </PageHelp>
 */
import { ref, watch } from "vue";
import { ChevronDown, ChevronRight, HelpCircle, X } from "lucide-vue-next";

const props = defineProps<{
  /** localStorage key. Persists collapsed state per-page. */
  storageKey: string;
  /** Заголовок панели. По умолчанию «Что это такое». */
  title?: string;
  /** Tone: info (default, синяя), tip (зелёная), warning (жёлтая). */
  tone?: "info" | "tip" | "warning";
  /** Default-open на первое посещение. По умолчанию open (чтобы первый раз увидеть). */
  defaultOpen?: boolean;
}>();

const titleText = props.title ?? "Что это и как пользоваться";
const tone = props.tone ?? "info";

const open = ref(loadInitial());

function loadInitial(): boolean {
  try {
    const raw = localStorage.getItem(props.storageKey);
    if (raw === "open") return true;
    if (raw === "closed") return false;
  } catch { /* ignore */ }
  return props.defaultOpen ?? true;
}

watch(open, (v) => {
  try { localStorage.setItem(props.storageKey, v ? "open" : "closed"); } catch { /* ignore */ }
});

const toneClasses: Record<string, { wrap: string; icon: string }> = {
  info: {
    wrap: "border-info-200 bg-info-50/40",
    icon: "text-info-600",
  },
  tip: {
    wrap: "border-success-200 bg-success-50/40",
    icon: "text-success-600",
  },
  warning: {
    wrap: "border-warning-200 bg-warning-50/40",
    icon: "text-warning-600",
  },
};
const cls = toneClasses[tone];
</script>

<template>
  <div
    class="border rounded-lg mb-4 transition-all"
    :class="cls.wrap"
  >
    <button
      type="button"
      class="w-full flex items-center gap-2 px-4 py-3 text-left"
      @click="open = !open"
    >
      <HelpCircle :class="cls.icon" class="size-4 shrink-0" />
      <span class="font-medium text-surface-900 text-sm">{{ titleText }}</span>
      <component :is="open ? ChevronDown : ChevronRight" class="size-4 shrink-0 text-surface-400 ml-auto" />
    </button>
    <div v-if="open" class="px-4 pb-4 pt-1 text-sm text-surface-700 space-y-2 prose-tight">
      <slot />
      <div class="pt-2 flex justify-end">
        <button
          type="button"
          class="text-xs text-surface-500 hover:text-surface-900 inline-flex items-center gap-1"
          @click="open = false"
        >
          <X class="size-3" /> Свернуть подсказку
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.prose-tight :deep(p) {
  margin: 0.25rem 0;
}
.prose-tight :deep(h4) {
  font-weight: 600;
  margin: 0.75rem 0 0.25rem;
  color: var(--color-text); /* was hardcoded dark rgb(15 23 42) → invisible on dark */
}
.prose-tight :deep(ul) {
  margin: 0.25rem 0;
  padding-left: 1.25rem;
  list-style: disc;
}
.prose-tight :deep(li) {
  margin: 0.125rem 0;
}
.prose-tight :deep(code) {
  /* Theme-aware: light chip on light, dark chip on dark — both readable.
     (Was hardcoded rgb(241 245 249) → light bg + light text = unreadable in dark.) */
  background: var(--color-bg-hover);
  color: var(--color-text);
  padding: 0.1rem 0.3rem;
  border-radius: 0.25rem;
  font-size: 0.8rem;
}
.prose-tight :deep(strong) {
  font-weight: 600;
}
</style>
